import React, {
  ChangeEventHandler,
  FocusEventHandler,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  WeakValidationMap,
} from 'react';
import Input from '../Input';
import PropTypes, { Validator } from 'prop-types';
import { forgeClassHelper } from '../utils/classes';
import FakeEvent from '../utils/fake-event';
import { optionalStringValidator } from '../utils/betterPropTypes';

const classes = forgeClassHelper('radio');

export type RadioValue = number | string | boolean;

export class RadioInputValue {
  constructor(public value: RadioValue, public text: string, public checked: boolean) {
    this.value = value;
    this.text = text;
    this.checked = checked;
  }
}
export type RadioInputOnChangeEvent = FakeEvent<RadioInputValue>;
export type RadioInputOnChangeEventHandler = (event: RadioInputOnChangeEvent) => void;
export interface RadioInputProps {
  /** Controlled Input box string value */
  inputText?: string;
  /** Callback called when either the radio checked state changes or its text changes */
  onChange?: RadioInputOnChangeEventHandler;
  /** Placeholder text for text field */
  placeholder?: string;
}

export interface RadioProps {
  /** Specify horizontal or vertical alignment */
  alignment?: 'horizontal' | 'vertical';
  /** Whether the Radio is selected; should only be used for [controlled](https://reactjs.org/docs/forms.html#controlled-components)  Radios */
  checked?: boolean;
  /** Radio labels can be set through the text prop or through children. */
  children?: React.ReactNode;
  /** Adds a class to the root element of the component */
  className?: string;
  /** Whether the Radio should be initially selected; should only be used for [uncontrolled](https://reactjs.org/docs/uncontrolled-components.html) Radios */
  defaultChecked?: boolean;
  /** Applies the disabled state class to the label and adds `disabled` attribute to the input element */
  disabled?: boolean;
  /** Highlights the input when defined (specific string doesn't matter) */
  error?: string;
  /** ID attribute for the Radio element */
  id: string;
  /**
   * A callback function bound to this component's input ref prop to trigger after the component is mounted or
   * unmounted.
   * inputRef will be called with the following parameters:
   *  input (Object):
   *  A mounted instance of the input
   */
  inputRef?: Ref<HTMLInputElement>;
  /** Name attribute. Should be the same as all other radio elements within a group */
  name?: string;
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio loses focus */
  onBlur?: FocusEventHandler<HTMLInputElement>;
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio value changes */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio receives focus */
  onFocus?: FocusEventHandler<HTMLInputElement>;
  /** Indicates whether this radio or any other in the same group is required to be checked */
  required?: boolean;
  tabIndex?: number;
  /** Label text shown next to the radio button */
  text?: string;
  /** Adds free-text radio option */
  textInputProps?: RadioInputProps;
  /** Value attribute of the radio input element. Should be unique within its RadioGroup. */
  value: RadioValue;
}
/**
 * Radio
 *
 * @documentedBy RadioGroup
 */
const Radio = React.forwardRef<HTMLInputElement, RadioProps>((props, ref) => {
  const {
    alignment,
    checked: upstreamChecked,
    children,
    className,
    defaultChecked,
    disabled,
    error,
    id,
    inputRef,
    name,
    onBlur,
    onChange,
    onFocus,
    required,
    tabIndex,
    text,
    textInputProps,
    value,
    ...inputProps
  } = props;
  const [controlledInputText, setControlledInputText] = useState('');
  const [controlledChecked, setControlledChecked] = useState(upstreamChecked || !!defaultChecked);
  const { inputText: upstreamInputText, onChange: onInputChange, placeholder } = textInputProps || {};

  const stringValue = useMemo(() => value.toString(), [value]);

  const inputText = useMemo(
    () => (upstreamInputText === undefined ? controlledInputText : upstreamInputText),
    [controlledInputText, upstreamInputText]
  );
  const checked = upstreamChecked === undefined ? controlledChecked : upstreamChecked;

  // using an internal ref and also forwarding it:
  const internalRef = useRef<HTMLInputElement>(null);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(inputRef, () => internalRef.current);
  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(ref, () => internalRef.current);

  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      if (onChange) {
        onChange(e);
      }
      setControlledChecked(e.target.checked);
      if (onInputChange) {
        onInputChange(
          new FakeEvent({
            value: new RadioInputValue(e.target.value, inputText, e.target.checked),
          })
        );
      }
    },
    [onChange, onInputChange, inputText]
  );

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setControlledInputText(e.target.value);
      if (onChange) {
        /** A user typing into the text box should cause its Radio to become
         * checked. Manufacture a new event by merging the original event and
         * the radio input target */
        onChange({ ...e, target: { ...e.target, ...internalRef.current, value: stringValue } });
      }
      if (onInputChange) {
        onInputChange(
          new FakeEvent({
            value: new RadioInputValue(stringValue, e.target.value, checked),
          })
        );
      }
    },
    [checked, onChange, onInputChange, stringValue]
  );

  return (
    <div
      {...classes({
        modifiers: {
          horizontal: alignment === 'horizontal',
        },
        extra: className,
      })}
    >
      <input
        type="radio"
        {...inputProps}
        id={id}
        {...classes('input')}
        name={name || id}
        value={stringValue}
        onChange={handleRadioChange}
        onFocus={onFocus}
        onBlur={onBlur}
        required={required}
        checked={checked}
        ref={internalRef}
        disabled={disabled}
        tabIndex={tabIndex}
      />
      <label
        {...classes({
          element: 'description',
          states: {
            disabled: !!disabled,
            error: !!error && !disabled,
          },
        })}
        htmlFor={id}
      >
        {text}
        {children}
        {textInputProps && (
          <Input
            id={id + '-input'}
            placeholder={placeholder}
            error={error}
            disabled={disabled}
            onChange={handleTextChange}
            value={inputText}
            {...inputProps}
          />
        )}
      </label>
    </div>
  );
});

Radio.displayName = 'Radio';

export const radioPropTypes: WeakValidationMap<RadioProps & { '...rest': unknown }> = {
  /** Specify horizontal or vertical alignment */
  alignment: PropTypes.oneOf(['horizontal', 'vertical']),
  /** Whether the Radio is selected; should only be used for [controlled](https://reactjs.org/docs/forms.html#controlled-components)  Radios */
  checked: PropTypes.bool,
  /** Radio labels can be set through the text prop or through children. */
  children: PropTypes.node,
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /** Whether the Radio should be initially selected; should only be used for [uncontrolled](https://reactjs.org/docs/uncontrolled-components.html) Radios */
  defaultChecked: PropTypes.bool,
  /** Applies the disabled state class to the label and adds `disabled` attribute to the input element */
  disabled: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** ID attribute for the Radio element */
  id: PropTypes.string.isRequired,
  /**
   * A callback function bound to this component's input ref prop to trigger after the component is mounted or
   * unmounted.
   * inputRef will be called with the following parameters:
   *  input (Object):
   *  A mounted instance of the input
   */
  inputRef: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.shape({
      current: PropTypes.any,
    }).isRequired,
  ]) as Validator<Ref<HTMLInputElement>>,
  /** Name attribute. Should be the same as all other radio elements within a group */
  name: PropTypes.string,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio loses focus */
  onBlur: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio value changes */
  onChange: PropTypes.func,
  /** Function that takes a JS event as an argument. Called when the RadioGroup or Radio receives focus */
  onFocus: PropTypes.func,
  /** Indicates whether this radio or any other in the same group is required to be checked */
  required: PropTypes.bool,
  /** Label text shown next to the radio button */
  text: PropTypes.string,
  /** Adds free-text radio option */
  textInputProps: PropTypes.shape({
    /** Controlled Input box string value */
    inputText: optionalStringValidator,
    /** Callback called when either the radio checked state changes or its text changes */
    onChange: PropTypes.func as Validator<RadioInputOnChangeEventHandler>,
    /** Placeholder text for text field */
    placeholder: optionalStringValidator,
  }),
  /** Value attribute of the radio input element. Should be unique within its RadioGroup. */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]).isRequired,
  /** Passthrough props to <div> that wraps the radio input and its label */
  '...rest': PropTypes.any,
};
Radio.propTypes = radioPropTypes;

export default Radio;
